/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useState } from 'react';

import { FormattedMessage, useIntl } from '@futrli/localisation';

import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';
import Icon from 'carbon-react/lib/components/icon';
import { Option, Select } from 'carbon-react/lib/components/select';
import Textbox from 'carbon-react/lib/components/textbox';

import { useReportBuilderContext } from '../../report-builder.context';
import { AllFieldsSelectDropdown } from './all-fields-select-dropdown';
import { DataField, MappingData } from '../../types';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';
import { useConsolidatedForecastingContext } from '../../../forecast/detail/context/consolidated-forecasting-context';
import { getConsolidationShortCode, getMissingDatasetName } from '../../utils-data';
import { useI18nConstants } from '../../use-i18n-constants';
import { UNAVAILABLE_DATASET_KIND } from '../../constants';
import { ReportingSidebar } from '../reporting-sidebar/reporting-sidebar';

import styles from './map-template-data-modal.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (origRefs: string[], mapRefs: string[]) => void;
}

type FieldType = 'category' | 'group' | 'account' | 'assumption' | 'nonFinancial' | 'formula';
const supportedTypes = ['category', 'group', 'account', 'assumption', 'nonFinancial', 'formula'];

/**
 * Mapping modal to map workspace datasets and data refs to those of the report
 */
export const MapTemplateDataModal = ({ isOpen, onClose, onSave }: Props) => {
  const intl = useIntl();
  // datasetsToMap or/and dataRefsToMap should be set before triggering the modal
  const {
    datasetsToMap,
    forecasts,
    dataRefsToMap,
    allFinFieldsArray,
    dataReferenceMapping,
    rowFormulaData,
    reportDsToMap
  } = useReportBuilderContext();
  const { dummyTemplateForecasts } = useI18nConstants();
  const { isConsolidation } = usePredictionsInfoContext();
  const { consolidation } = useConsolidatedForecastingContext();

  const forecastDisplayName = intl.formatMessage({ id: 'common.forecast', defaultMessage: 'Forecast' });

  const [datasetsMappedTo, setDatasetsMappedTo] = useState<string[]>([]);
  const [fieldsMappedTo, setFieldsMappedTo] = useState<{
    category: string[];
    group: string[];
    account: string[];
    assumption: string[];
    nonFinancial: string[];
    formula: string[];
  }>({
    category: [],
    group: [],
    account: [],
    assumption: [],
    nonFinancial: [],
    formula: []
  });

  const missingDatasetLabel = intl.formatMessage({
    id: 'reporting.missing_dataset',
    defaultMessage: 'Missing dataset'
  });

  // list of datasets to map: datasets coming from a futrli template, from a workspace template, and datasets that are
  // no longer available (missing)
  const datasets = useMemo(() => {
    const dsObjects = dataReferenceMapping?.dataset_references
      ? Object.keys(dataReferenceMapping.dataset_references).map(key => dataReferenceMapping?.dataset_references[key])
      : [];
    const datasetsToShow = dsObjects.filter(d => d?.data.kind !== 'live' && datasetsToMap.includes(d?.id));
    const missingDatasets = datasetsToShow
      .filter(d => d.data?.kind === UNAVAILABLE_DATASET_KIND)
      .map(d => ({ ...d, name: getMissingDatasetName(d.id, reportDsToMap, missingDatasetLabel) }));
    const workspaceTemplateDatasets = datasetsToShow
      .filter(d => d.data?.kind !== UNAVAILABLE_DATASET_KIND)
      .map((d, i, all) => ({
        ...d,
        name: all.length > 1 ? `${forecastDisplayName} ${i + 1}` : forecastDisplayName
      }));
    const futrliTemplateDatasets = dummyTemplateForecasts
      .filter(d => datasetsToMap.includes(d.id))
      .map(d => ({ id: d.id, name: d.name, type: 'dataset', data: { kind: d.kind } } as MappingData));
    return [...futrliTemplateDatasets, ...workspaceTemplateDatasets, ...missingDatasets];
  }, [
    dataReferenceMapping,
    datasetsToMap,
    dummyTemplateForecasts,
    forecastDisplayName,
    missingDatasetLabel,
    reportDsToMap
  ]);

  // true when there are datasets that are no longer available to map (and nothing else than datasets)
  const isUnavailableDatasetsMapping =
    !dataRefsToMap.length && datasets.some(d => d.data?.kind === UNAVAILABLE_DATASET_KIND);

  // list of data fields to map, by type
  const fieldsByCategory = useMemo(() => {
    const fieldObjects = Object.keys(dataReferenceMapping?.line_data_references || {}).map(key => ({
      ...dataReferenceMapping?.line_data_references[key],
      ref: key,
      // displayedName differs from name by adding -CF to cash flow accounts/predictions/groups
      displayedName: key.includes(':cash:')
        ? `${dataReferenceMapping?.line_data_references[key].name}-CF`
        : dataReferenceMapping?.line_data_references[key].name
    }));

    const statics = fieldObjects.filter(d => d.type === 'static' && dataRefsToMap.includes(d.ref));
    const groups = fieldObjects.filter(d => d.type === 'group' && dataRefsToMap.includes(d.ref));
    const accounts = fieldObjects.filter(d => d.type === 'account' && dataRefsToMap.includes(d.ref));
    const assumptions = fieldObjects.filter(d => d.type === 'assumption' && dataRefsToMap.includes(d.ref));
    const nonFinancial = fieldObjects.filter(d => d.type === 'non_financial' && dataRefsToMap.includes(d.ref));
    const formula = fieldObjects.filter(d => d.type === 'row_formula' && dataRefsToMap.includes(d.ref));
    return [
      {
        type: 'category',
        name: intl.formatMessage({ id: 'common.category', defaultMessage: 'Category' }),
        fields: statics
      },
      { type: 'group', name: intl.formatMessage({ id: 'common.group', defaultMessage: 'Group' }), fields: groups },
      {
        type: 'account',
        name: intl.formatMessage({ id: 'common.account', defaultMessage: 'Account' }),
        fields: accounts
      },
      {
        type: 'assumption',
        name: intl.formatMessage({ id: 'common.prediction', defaultMessage: 'Prediction' }),
        fields: assumptions
      },
      {
        type: 'nonFinancial',
        name: intl.formatMessage({ id: 'common.non_financial', defaultMessage: 'Non-financial' }),
        fields: nonFinancial
      },
      {
        type: 'formula',
        name: intl.formatMessage({ id: 'common.formula', defaultMessage: 'Formula' }),
        fields: formula
      }
    ];
  }, [dataReferenceMapping, dataRefsToMap, intl]);

  // This modal can be used to map only one datasets/group/account/prediction...
  // modalType is set to give it the right title
  const modalType = useMemo(() => {
    if (datasetsToMap.length === 1 && !dataRefsToMap.length) {
      return 'dataset';
    }
    if (dataRefsToMap.length === 1 && !datasetsToMap.length) {
      if (fieldsByCategory[0].fields.find(f => f.ref === dataRefsToMap[0])) {
        return 'category';
      }
      if (fieldsByCategory[1].fields.find(f => f.ref === dataRefsToMap[0])) {
        return 'group';
      }
      if (fieldsByCategory[2].fields.find(f => f.ref === dataRefsToMap[0])) {
        return 'account';
      }
      if (fieldsByCategory[3].fields.find(f => f.ref === dataRefsToMap[0])) {
        return 'prediction';
      }
      if (fieldsByCategory[4].fields.find(f => f.ref === dataRefsToMap[0])) {
        return 'non-financial';
      }
      if (fieldsByCategory[5].fields.find(f => f.ref === dataRefsToMap[0])) {
        return 'formula';
      }
    }
    return 'mixed';
  }, [datasetsToMap, dataRefsToMap, fieldsByCategory]);

  const displayedModalType = useMemo(() => {
    switch (modalType) {
      case 'dataset':
        return intl.formatMessage({ id: 'common.dataset', defaultMessage: 'Dataset' });
      case 'category':
        return intl.formatMessage({ id: 'common.category', defaultMessage: 'Category' });
      case 'group':
        return intl.formatMessage({ id: 'common.group', defaultMessage: 'Group' });
      case 'account':
        return intl.formatMessage({ id: 'common.account', defaultMessage: 'Account' });
      case 'prediction':
        return intl.formatMessage({ id: 'common.prediction', defaultMessage: 'Prediction' });
      case 'non-financial':
        return intl.formatMessage({ id: 'common.non_financial', defaultMessage: 'Non-financial' });
      case 'formula':
        return intl.formatMessage({ id: 'common.formula', defaultMessage: 'Formula' });
      default:
        return '';
    }
  }, [intl, modalType]);

  const displayedModalTypeWithArticle = useMemo(() => {
    switch (modalType) {
      case 'dataset':
        return intl.formatMessage({ id: 'common.a_dataset', defaultMessage: 'a dataset' });
      case 'category':
        return intl.formatMessage({ id: 'common.a_category', defaultMessage: 'a category' });
      case 'group':
        return intl.formatMessage({ id: 'common.a_group', defaultMessage: 'a group' });
      case 'account':
        return intl.formatMessage({ id: 'common.an_account', defaultMessage: 'an account' });
      case 'prediction':
        return intl.formatMessage({ id: 'common.a_prediction', defaultMessage: 'a prediction' });
      case 'non-financial':
        return intl.formatMessage({ id: 'common.a_non_financial', defaultMessage: 'a non-financial' });
      case 'formula':
        return intl.formatMessage({ id: 'common.a_formula', defaultMessage: 'a formula' });
      default:
        return '';
    }
  }, [intl, modalType]);

  // list of datasets to map to, will be displayed in the dropdown
  // (the actuals datasource is not part of the forecasts list, so there is no live forecast to exclude)
  const targetDatasets = forecasts;

  // list of fields to map to by type, will be used to auto select fields with the same name and type
  const targetFields = useMemo(() => {
    const category = isConsolidation ? allFinFieldsArray.filter(f => f.classifier === 'static') : [];
    const group = allFinFieldsArray.filter(f =>
      ['group', 'elimination-group', 'group-by-account-name', 'group-by-org-group-name'].includes(f.classifier)
    );
    const account = allFinFieldsArray.filter(f => f.classifier === 'account' || f.classifier === 'eliminated-account');
    const assumption = allFinFieldsArray.filter(f => f.classifier === 'assumption');
    const nonFinancial = allFinFieldsArray.filter(f =>
      ['driver-group', 'freestyle-driver', 'formula-driver'].includes(f.classifier)
    );
    return {
      category,
      group,
      account,
      assumption,
      nonFinancial,
      formula: (rowFormulaData as unknown) as DataField[]
    };
  }, [allFinFieldsArray, rowFormulaData, isConsolidation]);

  // initialize datasetsMappedTo, datasets with the same name should be auto selected
  useEffect(() => {
    const selectedDSs = datasets.map(() => '');
    datasets.forEach((ds, i) => {
      const sameNameDs = targetDatasets.find(d => d.name === ds.name);
      if (sameNameDs) {
        selectedDSs[i] = sameNameDs.id;
      }
    });
    setDatasetsMappedTo(selectedDSs);
  }, [datasets, targetDatasets]);

  // initialize fieldsMappedTo, fields withe same name and type should be auto selected
  // for consolidations, it should also take into account the consolidation id
  useEffect(() => {
    const mappedTo = {
      category: fieldsByCategory[5].fields.map(() => ''),
      group: fieldsByCategory[0].fields.map(() => ''),
      account: fieldsByCategory[1].fields.map(() => ''),
      assumption: fieldsByCategory[2].fields.map(() => ''),
      nonFinancial: fieldsByCategory[3].fields.map(() => ''),
      formula: fieldsByCategory[4].fields.map(() => '')
    };
    supportedTypes.forEach((type, i) => {
      fieldsByCategory[i].fields.forEach((f, i) => {
        const sameName = (targetFields[type as FieldType] || []).find(e =>
          isConsolidation
            ? e.name === f.name &&
              getConsolidationShortCode(consolidation, e.organisation_id || '') === f.data?.organisation_short_code
            : e.name === f.name
        );
        if (sameName) {
          mappedTo[type as FieldType][i] = sameName.ref;
        }
      });
    });
    setFieldsMappedTo(mappedTo);
  }, [fieldsByCategory, targetFields, consolidation, isConsolidation]);

  // updating state when user changes or selects a dataset
  const handleDSChange = (index: number, ds: string) => {
    const newDS = [...datasetsMappedTo];
    newDS[index] = ds;
    setDatasetsMappedTo(newDS);
  };

  // updating state when user changes or selects a data field
  const handleFieldChange = (type: FieldType, index: number, ref: string) => {
    const newFMT = { ...fieldsMappedTo };
    newFMT[type] = [...newFMT[type]];
    newFMT[type][index] = ref;
    setFieldsMappedTo(newFMT);
  };

  // save button action, we call provided onSave method and give it the refs we want to map, and the refs to map to
  const handleSaveDatasetsMapping = () => {
    const origRefs: string[] = [];
    const mapRefs: string[] = [];
    datasets.forEach((d, i) => {
      if (datasetsMappedTo[i]) {
        origRefs.push(d.id);
        mapRefs.push(datasetsMappedTo[i]);
      }
    });
    supportedTypes.forEach((type, i) => {
      fieldsMappedTo[type as FieldType].forEach((f, j) => {
        if (f) {
          origRefs.push(fieldsByCategory[i].fields[j].ref);
          mapRefs.push(f);
        }
      });
    });
    onSave(origRefs, mapRefs);
    onClose();
  };

  return (
    <ReportingSidebar
      aria-label={intl.formatMessage({
        id: 'reporting.map_workspace_template_data',
        defaultMessage: 'Map workspace template data'
      })}
      title={
        isUnavailableDatasetsMapping
          ? intl.formatMessage({
              id: 'reporting.map_unavailable_datasets',
              defaultMessage: 'Map unavailable datasets'
            })
          : modalType === 'mixed'
          ? intl.formatMessage({
              id: 'reporting.map_workspace_template_data',
              defaultMessage: 'Map workspace template data'
            })
          : modalType === 'dataset'
          ? intl.formatMessage({
              id: 'reporting.map_workspace_template_data_set',
              defaultMessage: 'Map workspace template data set'
            })
          : intl.formatMessage(
              { id: 'reporting.map_workspace_template_by_type', defaultMessage: 'Map workspace template {modalType}' },
              { modalType: displayedModalType }
            )
      }
      open={isOpen}
      onCancel={onClose}
      sidebarProps={{
        size: 'extra-large'
      }}
    >
      <Form
        stickyFooter
        rightSideButtons={
          <>
            <Button onClick={onClose} buttonType="secondary">
              <FormattedMessage id="reporting.close_without_maping" defaultMessage="Close without mapping" />
            </Button>
            <Button onClick={handleSaveDatasetsMapping} buttonType="primary">
              <FormattedMessage id="common.save" defaultMessage="Save" />
            </Button>
          </>
        }
        onSubmit={() => null}
      >
        <div className={styles.mapModalContent}>
          <p className={styles.intro}>
            {isUnavailableDatasetsMapping ? (
              <>
                <FormattedMessage
                  id="reporting.map_unavailable_datasets_description_line_1"
                  defaultMessage="Some datasets used in this report are not available. Please map them to an available dataset."
                />
                <br />
                <FormattedMessage
                  id="reporting.map_unavailable_datasets_description_line_2"
                  defaultMessage="You can come back to this step at any time, or remove the datasets from the widgets."
                />
              </>
            ) : modalType === 'mixed' ? (
              <>
                <FormattedMessage
                  id="reporting.map_workspace_template_data_description_line_1"
                  defaultMessage="This report can't match the names of some data in this template. Please map them."
                />
                <br />
                <FormattedMessage
                  id="reporting.map_workspace_template_data_description_line_2"
                  defaultMessage="You can come back to this step at any time, or delete widgets in the next step."
                />
              </>
            ) : modalType === 'dataset' ? (
              <FormattedMessage
                id="reporting.map_workspace_template_dataset_description_v2"
                defaultMessage="This widget can't find a dataset. Please replace or delete the lost dataset in the side panel."
              />
            ) : (
              <FormattedMessage
                id="reporting.map_workspace_template_by_type_description_v2"
                defaultMessage="This widget can't find {modalTypeWithArticle}. Please replace or delete the lost {modalType} in the side panel."
                values={{ modalType: displayedModalType, modalTypeWithArticle: displayedModalTypeWithArticle }}
              />
            )}
          </p>
          {datasets.length > 0 && (
            <div className={styles.mappingSection}>
              <div className={styles.leftSide}>
                <h3>
                  <FormattedMessage id="reporting.datasets_referenced" defaultMessage="Dataset(s) referenced" />
                </h3>
                {datasets.map(ds => (
                  <div key={`source-ds-${ds.id}`}>
                    <Textbox autoComplete="off" value={ds.name} onChange={() => undefined} disabled size="medium" />
                    <Icon type="arrow" />
                  </div>
                ))}
              </div>
              <div className={styles.rightSide}>
                <h3>
                  <FormattedMessage id="reporting.map_to" defaultMessage="Map to" />
                </h3>
                {datasetsMappedTo.length > 0 &&
                  datasets.map((ds, i) => (
                    <Select
                      key={`target-${ds.id}`}
                      autoComplete="off"
                      value={datasetsMappedTo[i]}
                      onChange={e => handleDSChange(i, e.target.value)}
                    >
                      {targetDatasets.length > 0 && <Option value="" text="" />}
                      {targetDatasets.map(tds => (
                        <Option key={`option-${tds.id}`} value={tds.id} text={tds.name} />
                      ))}
                      {targetDatasets.length === 0 && (
                        <Option
                          key="no-dataset"
                          value=""
                          text={intl.formatMessage({
                            id: 'reporting.no_dataset_to_map_to',
                            defaultMessage: 'No dataset to map to'
                          })}
                        />
                      )}
                    </Select>
                  ))}
              </div>
            </div>
          )}
          {fieldsByCategory.map(
            category =>
              category.fields.length > 0 && (
                <div className={styles.mappingSection} key={category.type}>
                  <div className={styles.leftSide}>
                    <h3>
                      <FormattedMessage
                        id="reporting.category_value_referenced"
                        defaultMessage="{categoryName}(s) referenced"
                        values={{ categoryName: category.name }}
                      />
                    </h3>
                    {category.fields.map(f => (
                      <div key={`source-field-${f.ref}`}>
                        <Textbox
                          value={
                            isConsolidation && f.data?.organisation_short_code
                              ? `${f.displayedName} - ${f.data?.organisation_short_code}`
                              : f.displayedName
                          }
                          onChange={() => undefined}
                          disabled
                          size="medium"
                        />
                        <Icon type="arrow" />
                      </div>
                    ))}
                  </div>
                  <div className={styles.rightSide}>
                    <h3>
                      <FormattedMessage id="reporting.map_to" defaultMessage="Map to" />
                    </h3>

                    {category.fields.map((f, i) => {
                      const type = category.type as FieldType;
                      const hasFields = (targetFields[type] || []).length > 0;
                      return (
                        <AllFieldsSelectDropdown
                          key={f.ref}
                          value={fieldsMappedTo[type][i]}
                          onChange={e => handleFieldChange(type, i, e)}
                          defaultText={
                            type === 'group' && !hasFields
                              ? intl.formatMessage({
                                  id: 'reporting.no_groups_found',
                                  defaultMessage: 'No groups found'
                                })
                              : ''
                          }
                          isFormula={type === 'formula'}
                        />
                      );
                    })}
                  </div>
                </div>
              )
          )}
        </div>
      </Form>
    </ReportingSidebar>
  );
};
